import { sendReport, extractReportIdFromResponse } from "../../functions/sendReport";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const USER_DATA_KEY = "userData";

async function post(path, adminToken, body) {
  const res = await fetch(`${BACKEND_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${adminToken}`,
    },
    body: JSON.stringify(body),
  });
  return res.json();
}

/** Fetches round1-5 completion + running points/time, and makes sure a `result` row exists for this player. */
export function fetchModakStatus(adminToken, { userId, email, name, userType }) {
  return post("/modakStatus", adminToken, { userId, email, name, userType });
}

/**
 * Push points/time to the external reports server (add or update), then
 * persist the returned reportId on the local `result` row — same flow as
 * states GamePage.pushExternalReport.
 */
export async function pushModakExternalReport(adminToken, user, { points, time }) {
  const role = user?.role || user?.source;
  if (!user?.userId || role === "demobypass" || role === "DEMO") return null;

  const reportId = user.reportId || user.report_id || null;
  const timeFormatted =
    typeof time === "string" && time.includes(":")
      ? time.startsWith("00:")
        ? time
        : `00:${time}`
      : `00:${time || "00:00"}`;

  try {
    const response = await sendReport({
      sessionId: user.sessionId,
      organizationId: user.organizationId,
      userId: user.userId,
      role,
      token: user.token,
      gameId: user.gameId,
      name: user.name || "",
      points,
      time: timeFormatted,
      reportId: reportId || undefined,
    });

    const newReportId = extractReportIdFromResponse(response);
    if (newReportId && newReportId !== reportId) {
      try {
        await post("/modakLinkReport", adminToken, {
          userId: user.userId,
          sessionId: user.sessionId,
          organizationId: user.organizationId,
          reportId: newReportId,
        });
      } catch (linkErr) {
        console.error("Failed to link reportId:", linkErr);
      }

      try {
        const stored = JSON.parse(sessionStorage.getItem(USER_DATA_KEY) || "{}");
        const merged = { ...stored, ...user, reportId: newReportId };
        delete merged.report_id;
        sessionStorage.setItem(USER_DATA_KEY, JSON.stringify(merged));
      } catch (_) {
        /* ignore */
      }
      return newReportId;
    }
    return reportId;
  } catch (error) {
    console.error("Failed to send external report:", error);
    return reportId;
  }
}

/** Records one round's answers/points/time (server no-ops if this round was already submitted). */
export async function submitModakRound(
  adminToken,
  { userId, round, points, time, answers, user }
) {
  const result = await post("/modakSubmitRound", adminToken, {
    userId,
    round,
    points,
    time,
    answers,
  });

  // The server already saw this round's row exists and no-op'd the insert —
  // meaning the external report push for it already happened the first
  // time. Pushing again here (e.g. a remount via browser back/forward or
  // bfcache resetting an in-memory "already submitted" ref) would POST a
  // second, duplicate report instead of updating the existing one.
  if (user && !result?.alreadySubmitted) {
    await pushModakExternalReport(adminToken, user, { points, time });
  }

  return result;
}
